import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Stop, Add, Edit, Delete, Bolt } from "@mui/icons-material";
import { useTracker } from "@/hooks/useTracker";
import { formatDuration } from "@/utils/format";
import { PAGE_MAX_WIDTH } from "@/constants";
import { PageLoading } from "@/components/common/PageLoading";
import { EmptyTableRow } from "@/components/common/EmptyTableRow";
import { TableHeaderCell } from "@/components/common/TableHeaderCell";
import { PulseDot } from "@/components/common/PulseDot";

export function TrackerPage() {
  const { t } = useTranslation();
  const tracker = useTracker();

  if (tracker.loading) return <PageLoading />;

  return (
    <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: "auto" }}>
      <ActiveSessionCard tracker={tracker} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          {t("tracker.todayEntries")}
        </Typography>
      </Box>
      <EntriesTable
        entries={tracker.completedEntries}
        onDelete={tracker.handleDelete}
      />
      <Fab
        color="primary"
        sx={{ position: "fixed", bottom: 32, right: 32 }}
        onClick={() => tracker.setDialogOpen(true)}
      >
        <Add />
      </Fab>
      <ManualEntryDialog tracker={tracker} />
    </Box>
  );
}

function ActiveSessionCard({
  tracker,
}: {
  tracker: ReturnType<typeof useTracker>;
}) {
  const { t } = useTranslation();
  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Bolt fontSize="small" color="primary" />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {t("tracker.activeSession")}
        </Typography>
      </Box>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: 2,
          }}
        >
          <TextField
            label={t("tracker.whatWorking")}
            value={
              tracker.active ? tracker.active.description || "" : tracker.desc
            }
            onChange={(e) => tracker.setDesc(e.target.value)}
            disabled={!!tracker.active}
            fullWidth
            size="small"
          />
          <TextField
            label={t("tracker.selectProject")}
            value={
              tracker.active ? tracker.active.projectId : tracker.projectId
            }
            onChange={(e) => tracker.setProjectId(e.target.value)}
            disabled={!!tracker.active}
            select
            sx={{ minWidth: 200 }}
            size="small"
          >
            {tracker.projects.map((p: any) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 2,
              py: 1,
              bgcolor: "action.hover",
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
            }}
          >
            {tracker.active && <PulseDot />}
            <Typography
              variant="h5"
              sx={{
                fontFamily: "monospace",
                fontWeight: 700,
              }}
            >
              {tracker.active
                ? formatDuration(tracker.active.from)
                : "00:00:00"}
            </Typography>
            {tracker.active ? (
              <IconButton
                onClick={tracker.handleStop}
                sx={{
                  bgcolor: "error.main",
                  color: "white",
                  "&:hover": { bgcolor: "error.dark" },
                }}
              >
                <Stop />
              </IconButton>
            ) : (
              <Button
                variant="contained"
                onClick={tracker.handleStart}
                disabled={!tracker.canStartTimer}
              >
                {t("tracker.startTimer")}
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function EntriesTable({
  entries,
  onDelete,
}: {
  entries: any[];
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Card variant="outlined">
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "action.hover" }}>
              <TableHeaderCell>{t("tracker.description")}</TableHeaderCell>
              <TableHeaderCell>{t("tracker.project")}</TableHeaderCell>
              <TableHeaderCell>{t("tracker.duration")}</TableHeaderCell>
              <TableHeaderCell>{t("projects.status")}</TableHeaderCell>
              <TableHeaderCell align="right">
                {t("common.actions")}
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry: any) => (
              <TableRow key={entry.id} hover>
                <TableCell>
                  <Typography
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {entry.description || "-"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {new Date(entry.from).toLocaleTimeString()} -{" "}
                    {new Date(entry.through).toLocaleTimeString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={entry.project?.name || "-"}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontWeight: 700,
                      fontSize: 10,
                      textTransform: "uppercase",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: 500,
                    }}
                  >
                    {formatDuration(entry.from, entry.through)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      entry.billable
                        ? t("tracker.billable")
                        : t("tracker.nonBillable")
                    }
                    size="small"
                    color={entry.billable ? "default" : "secondary"}
                    variant="outlined"
                    sx={{ fontSize: 10 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small">
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(entry.id)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {entries.length === 0 && <EmptyTableRow colSpan={5} />}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

function ManualEntryDialog({
  tracker,
}: {
  tracker: ReturnType<typeof useTracker>;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={tracker.dialogOpen}
      onClose={() => tracker.setDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("tracker.quickEntry")}</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
      >
        <TextField
          label={t("tracker.description")}
          value={tracker.manualDesc}
          onChange={(e) => tracker.setManualDesc(e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          label={t("tracker.project")}
          value={tracker.manualProject}
          onChange={(e) => tracker.setManualProject(e.target.value)}
          select
          fullWidth
          margin="dense"
        >
          {tracker.projects.map((p: any) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t("tracker.from")}
          type="datetime-local"
          value={tracker.manualFrom}
          onChange={(e) => tracker.setManualFrom(e.target.value)}
          fullWidth
          margin="dense"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label={t("tracker.through")}
          type="datetime-local"
          value={tracker.manualThrough}
          onChange={(e) => tracker.setManualThrough(e.target.value)}
          fullWidth
          margin="dense"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={tracker.manualBillable}
              onChange={(e) => tracker.setManualBillable(e.target.checked)}
            />
          }
          label={t("tracker.billable")}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => tracker.setDialogOpen(false)}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={tracker.handleManualCreate}
          disabled={!tracker.canCreateManual}
        >
          {t("common.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
